/**
 * MODULE: domain/pages/frontend/components/editor/pagesBlockDragHandlePlugin.ts
 * PURPOSE: Notion-style + / drag handle for Pages note editor (TipTap v2)
 * AGENT SAFETY: 🟡 RESTRICTED
 * RECENT: 2026-05-24 [2.2.1186] — + block menu; sticky hover delay so toolbar stays clickable
 */

import { NodeRangeSelection } from '@tiptap/extension-node-range';
import type { Editor } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import type { BlockAddCommandCallbacks } from '../../utils/blockInsertUtils';
import {
  isNestedDraggableTarget,
  resolveDraggableBlockAtPos,
  type DraggableBlockTarget,
} from '../../utils/pagesBlockDragTarget';
import {
  createBlockAddMenuPanel,
  renderBlockAddMenu,
} from './pagesBlockAddMenu';

export const pagesBlockDragHandlePluginKey = new PluginKey('pagesBlockDragHandle');

const HANDLE_HIDE_DELAY_MS = 400;

const TOOLBAR_CLASS =
  'pages-block-handle-toolbar pointer-events-auto flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5 shadow-sm dark:border-gray-700 dark:bg-gray-900';

const ADD_TRIGGER_CLASS =
  'pages-block-add-trigger flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200';

const HANDLE_CLASS =
  'pages-block-drag-handle flex h-7 w-7 cursor-grab items-center justify-center rounded text-gray-400 active:cursor-grabbing hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200';

const COMMENT_BUTTON_CLASS =
  'pages-block-comment-button relative flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-gray-800 dark:hover:text-blue-300 disabled:cursor-not-allowed disabled:opacity-40';

const PLUS_ICON =
  '<svg class="h-4 w-4" aria-hidden="true" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>';

const GRIP_ICON =
  '<svg class="h-4 w-4" aria-hidden="true" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/></svg>';

type PluginOptions = {
  editor: Editor;
  tippyOptions?: Record<string, unknown>;
  onTargetChange?: (target: DraggableBlockTarget | null) => void;
  onBlockCommentRequest?: (target: DraggableBlockTarget) => void;
  blockCommentCounts?: Record<string, number>;
  getBlockCommentCounts?: () => Record<string, number>;
  blockAddCallbacks: BlockAddCommandCallbacks;
};

type HandleToolbar = {
  toolbar: HTMLDivElement;
  commentButton: HTMLButtonElement;
  dragHandle: HTMLButtonElement;
  addTrigger: HTMLButtonElement;
  menuShell: HTMLDivElement;
  menuScroll: HTMLDivElement;
};

function createHandleToolbar(): HandleToolbar {
  const toolbar = document.createElement('div');
  toolbar.className = TOOLBAR_CLASS;
  toolbar.setAttribute('data-testid', 'pages-block-handle-toolbar');

  const addTrigger = document.createElement('button');
  addTrigger.type = 'button';
  addTrigger.className = ADD_TRIGGER_CLASS;
  addTrigger.setAttribute('data-testid', 'pages-block-add-trigger');
  addTrigger.setAttribute('aria-label', 'Add block');
  addTrigger.setAttribute('aria-haspopup', 'menu');
  addTrigger.innerHTML = PLUS_ICON;

  const dragHandle = document.createElement('button');
  dragHandle.type = 'button';
  dragHandle.className = HANDLE_CLASS;
  dragHandle.setAttribute('data-testid', 'pages-block-drag-handle');
  dragHandle.setAttribute('aria-label', 'Drag block');
  dragHandle.innerHTML = GRIP_ICON;

  const commentButton = document.createElement('button');
  commentButton.type = 'button';
  commentButton.className = COMMENT_BUTTON_CLASS;
  commentButton.setAttribute('data-testid', 'pages-block-comment-button');
  commentButton.setAttribute('aria-label', 'Comment on block');
  commentButton.innerHTML =
    '<svg class="h-3.5 w-3.5" aria-hidden="true" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h8M8 14h5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>';

  const { shell: menuShell, scroll: menuScroll } = createBlockAddMenuPanel();

  toolbar.append(addTrigger, dragHandle, commentButton);
  return { toolbar, commentButton, dragHandle, addTrigger, menuShell, menuScroll };
}

function blockDomNode(view: EditorView, target: DraggableBlockTarget): HTMLElement | null {
  const dom = view.nodeDOM(target.pos);
  if (!dom || dom === view.dom || dom.nodeType !== 1) {
    return null;
  }
  return dom as HTMLElement;
}

function startBlockDrag(event: DragEvent, editor: Editor, target: DraggableBlockTarget) {
  const { view } = editor;
  if (!event.dataTransfer) {
    return;
  }

  const from = target.pos;
  const selection = NodeRangeSelection.create(view.state.doc, from, from + target.node.nodeSize);
  const slice = selection.content();
  const element = view.nodeDOM(from);
  const preview = element instanceof HTMLElement ? element.cloneNode(true) : null;
  const wrapper = document.createElement('div');

  if (preview instanceof HTMLElement) {
    preview.style.pointerEvents = 'none';
    wrapper.append(preview);
  }
  wrapper.style.position = 'absolute';
  wrapper.style.top = '-10000px';
  document.body.append(wrapper);

  event.dataTransfer.clearData();
  event.dataTransfer.setDragImage(wrapper, 0, 0);
  view.dragging = { slice, move: true };

  const { tr } = view.state;
  tr.setSelection(selection);
  view.dispatch(tr);

  document.addEventListener(
    'drop',
    () => {
      wrapper.remove();
    },
    { once: true }
  );
}

function resolveTargetFromEvent(view: EditorView, clientX: number, clientY: number) {
  const rect = view.dom.getBoundingClientRect();
  const coords = view.posAtCoords({
    left: Math.min(Math.max(clientX, rect.left + 1), rect.right - 1),
    top: clientY,
  });
  if (!coords) {
    return null;
  }
  return resolveDraggableBlockAtPos(view.state.doc, coords.pos);
}

function targetsEqual(a: DraggableBlockTarget | null, b: DraggableBlockTarget | null): boolean {
  if (!a || !b) {
    return a === b;
  }
  return a.pos === b.pos && a.node.eq(b.node);
}

function syncHandleTestId(
  dragHandle: HTMLButtonElement,
  doc: EditorView['state']['doc'],
  target: DraggableBlockTarget | null
) {
  const nested = target ? isNestedDraggableTarget(doc, target) : false;
  const testId = nested ? 'pages-block-drag-handle-nested' : 'pages-block-drag-handle';
  dragHandle.dataset.testid = testId;
  dragHandle.setAttribute('data-testid', testId);
  dragHandle.setAttribute('aria-label', nested ? 'Drag list item' : 'Drag block');
}

export function createPagesBlockDragHandlePlugin({
  editor,
  tippyOptions,
  onTargetChange,
  onBlockCommentRequest,
  blockCommentCounts = {},
  getBlockCommentCounts,
  blockAddCallbacks,
}: PluginOptions): Plugin {
  const resolveBlockCommentCounts = () => getBlockCommentCounts?.() ?? blockCommentCounts;
  const { toolbar, commentButton, dragHandle, addTrigger, menuShell, menuScroll } =
    createHandleToolbar();
  const wrapper = document.createElement('div');
  let handlePopup: TippyInstance | null = null;
  let menuPopup: TippyInstance | null = null;
  let currentTarget: DraggableBlockTarget | null = null;
  let hideTimer: number | null = null;
  let menuOpen = false;
  let toolbarHovered = false;

  const clearHideTimer = () => {
    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const hideToolbar = () => {
    if (menuOpen || toolbarHovered) {
      return;
    }
    menuPopup?.hide();
    handlePopup?.hide();
    currentTarget = null;
    syncHandleTestId(dragHandle, editor.view.state.doc, null);
    syncCommentButton(null);
    onTargetChange?.(null);
  };

  const scheduleHide = () => {
    if (menuOpen || toolbarHovered) {
      return;
    }
    clearHideTimer();
    hideTimer = window.setTimeout(hideToolbar, HANDLE_HIDE_DELAY_MS);
  };

  const cancelHide = () => {
    clearHideTimer();
  };

  const closeAddMenu = () => {
    menuPopup?.hide();
  };

  addTrigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentTarget || !menuPopup) {
      return;
    }
    renderBlockAddMenu(editor, menuScroll, currentTarget, blockAddCallbacks, closeAddMenu);
    menuScroll.focus({ preventScroll: true });
    menuPopup.show();
  });

  addTrigger.addEventListener('mousedown', (event) => {
    event.stopPropagation();
  });

  commentButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentTarget || commentButton.disabled) {
      return;
    }
    closeAddMenu();
    onBlockCommentRequest?.(currentTarget);
  });

  commentButton.addEventListener('mousedown', (event) => {
    event.stopPropagation();
  });

  const syncCommentButton = (target: DraggableBlockTarget | null) => {
    const blockId = target?.node.attrs?.pagesBlockId;
    const hasBlockId = typeof blockId === 'string' && blockId.length > 0;
    commentButton.disabled = !hasBlockId;
    commentButton.title = hasBlockId
      ? 'Comment on this block'
      : 'Save the page before commenting on this block';
    const count = hasBlockId ? resolveBlockCommentCounts()[blockId] ?? 0 : 0;
    commentButton.dataset.commentCount = String(count);
    if (count > 0) {
      commentButton.classList.add('text-blue-600');
    } else {
      commentButton.classList.remove('text-blue-600');
    }
  };

  dragHandle.addEventListener('dragstart', (event) => {
    closeAddMenu();
    if (!currentTarget) {
      return;
    }
    startBlockDrag(event, editor, currentTarget);
    window.setTimeout(() => {
      dragHandle.style.pointerEvents = 'none';
    }, 0);
  });

  dragHandle.addEventListener('dragend', () => {
    dragHandle.style.pointerEvents = 'auto';
  });

  return new Plugin({
    key: pagesBlockDragHandlePluginKey,
    view(view) {
      dragHandle.draggable = true;
      document.body.appendChild(wrapper);
      wrapper.appendChild(toolbar);
      wrapper.style.pointerEvents = 'none';
      wrapper.style.position = 'absolute';
      wrapper.style.top = '0';
      wrapper.style.left = '0';
      wrapper.className = 'pages-block-drag-handle-layer';

      const bindHoverKeepAlive = (element: HTMLElement) => {
        element.addEventListener('pointerenter', () => {
          toolbarHovered = true;
          cancelHide();
        });
        element.addEventListener('pointerleave', () => {
          toolbarHovered = false;
          scheduleHide();
        });
      };

      bindHoverKeepAlive(toolbar);
      bindHoverKeepAlive(menuShell);
      bindHoverKeepAlive(menuScroll);

      menuPopup = tippy(addTrigger, {
        content: menuShell,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        appendTo: () => document.body,
        maxWidth: 'none',
        onCreate(instance) {
          instance.popper.addEventListener(
            'wheel',
            (event) => {
              event.stopPropagation();
            },
            { passive: true }
          );
        },
        onShow: () => {
          menuOpen = true;
          cancelHide();
        },
        onHide: () => {
          menuOpen = false;
          scheduleHide();
        },
      });

      handlePopup = tippy(view.dom, {
        getReferenceClientRect: () => view.dom.getBoundingClientRect(),
        interactive: true,
        trigger: 'manual',
        placement: 'left-start',
        hideOnClick: false,
        duration: 100,
        appendTo: () => document.body,
        popperOptions: {
          modifiers: [
            { name: 'flip', enabled: false },
            {
              name: 'preventOverflow',
              options: { rootBoundary: 'document', mainAxis: false },
            },
            {
              name: 'offset',
              options: { offset: [0, 6] },
            },
          ],
        },
        ...tippyOptions,
        content: toolbar,
      });

      return {
        destroy() {
          clearHideTimer();
          handlePopup?.destroy();
          menuPopup?.destroy();
          wrapper.remove();
        },
      };
    },
    props: {
      handleDOMEvents: {
        mouseleave(view, event) {
          const related = event.relatedTarget;
          if (related instanceof Node) {
            if (wrapper.contains(related) || menuShell.contains(related)) {
              return false;
            }
            const tippyRoot = (related as HTMLElement).closest?.('[data-tippy-root]');
            if (tippyRoot?.contains(related)) {
              return false;
            }
          }
          scheduleHide();
          return false;
        },
        mousemove(view, event) {
          if (!handlePopup) {
            return false;
          }

          const nextTarget = resolveTargetFromEvent(view, event.clientX, event.clientY);
          if (!nextTarget) {
            scheduleHide();
            return false;
          }

          const domNode = blockDomNode(view, nextTarget);
          if (!domNode) {
            return false;
          }

          cancelHide();

          if (!targetsEqual(currentTarget, nextTarget)) {
            closeAddMenu();
            currentTarget = nextTarget;
            syncHandleTestId(dragHandle, view.state.doc, nextTarget);
            syncCommentButton(nextTarget);
            onTargetChange?.(nextTarget);
            handlePopup.setProps({
              getReferenceClientRect: () => domNode.getBoundingClientRect(),
            });
            handlePopup.show();
          }

          return false;
        },
      },
    },
  });
}
